/**
 * @module ArtefactBrowser
 * @purpose Browser component for VQL artefacts with expandable category list
 * @depends VqlArtefact, VqlArtefactParameter types
 * @dependedOnBy CollectorSettingsForm, VelociraptorConfigModal
 *
 * @rules
 * - Performance: virtual-scroll not needed; artefact counts stay < 500
 * - State: local useState for expanded/search state
 * - Errors: no async ops; parent handles loading state
 */

import { useState, useMemo, useCallback, type ReactElement } from 'react'
import type { VqlArtefact } from '../../types/velociraptor'
import { BrowserToolbar, BrowserBody } from './ArtefactBrowser.parts'

interface ArtefactBrowserProps {
  artefacts: VqlArtefact[]
  artefactsByCategory: Record<string, VqlArtefact[]>
  selectedArtefacts: string[]
  parameterOverrides?: Record<string, Record<string, string | number | boolean>>
  loading?: boolean
  onSelect: (artefactName: string) => void
  onDeselect: (artefactName: string) => void
  onParameterChange?: (artefactName: string, paramName: string, value: string) => void
  onRefresh?: () => void
}

function toggleSetItem(prev: Set<string>, item: string): Set<string> {
  const next = new Set(prev)
  if (next.has(item)) { next.delete(item) } else { next.add(item) }
  return next
}

function filterArtefacts(artefacts: VqlArtefact[], term: string): VqlArtefact[] {
  const lower = term.toLowerCase()
  return artefacts.filter(
    (art) =>
      art.name.toLowerCase().includes(lower) ||
      (art.description?.toLowerCase().includes(lower) ?? false) ||
      (art.category?.toLowerCase().includes(lower) ?? false)
  )
}

export function ArtefactBrowser({
  artefacts,
  artefactsByCategory,
  selectedArtefacts,
  parameterOverrides = {},
  loading = false,
  onSelect,
  onDeselect,
  onParameterChange,
  onRefresh,
}: ArtefactBrowserProps): ReactElement {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [expandedArtefacts, setExpandedArtefacts] = useState<Set<string>>(new Set())

  const filteredArtefacts = useMemo(
    () => (searchTerm ? filterArtefacts(artefacts, searchTerm) : null),
    [artefacts, searchTerm]
  )
  const sortedCategories = useMemo(
    () => Object.keys(artefactsByCategory).sort(),
    [artefactsByCategory]
  )

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => toggleSetItem(prev, category))
  }, [])

  const toggleArtefactExpand = useCallback((name: string) => {
    setExpandedArtefacts((prev) => toggleSetItem(prev, name))
  }, [])

  const expandAll = useCallback((): void => {
    setExpandedCategories(new Set(sortedCategories))
  }, [sortedCategories])

  const collapseAll = useCallback((): void => {
    setExpandedCategories(new Set())
  }, [])

  const handleToggleArtefact = useCallback(
    (artefactName: string) => {
      if (selectedArtefacts.includes(artefactName)) onDeselect(artefactName)
      else onSelect(artefactName)
    },
    [selectedArtefacts, onSelect, onDeselect]
  )

  return (
    <div className="flex h-full flex-col">
      <BrowserToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCount={selectedArtefacts.length}
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
        loading={loading}
        onRefresh={onRefresh}
      />
      <div className="flex-1 overflow-y-auto">
        <BrowserBody
          loading={loading}
          searchTerm={searchTerm}
          filteredArtefacts={filteredArtefacts}
          sortedCategories={sortedCategories}
          artefactsByCategory={artefactsByCategory}
          expandedCategories={expandedCategories}
          selectedArtefacts={selectedArtefacts}
          expandedArtefacts={expandedArtefacts}
          parameterOverrides={parameterOverrides}
          onToggleCategory={toggleCategory}
          onToggleSelect={handleToggleArtefact}
          onToggleExpand={toggleArtefactExpand}
          onParameterChange={onParameterChange}
        />
      </div>
    </div>
  )
}
